import React from "react";
import { Check, ArrowRight, Star, MessageCircle } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";

const packages = [
  {
    id: "initiate",
    tag: "Initiate",
    accent: "#3b6cc0",
    bestFor: "Best for: Slow or inconsistent lead response",
    outcome: "100% lead response within minutes",
    strike: "₹7,999",
    price: "₹3,999",
    priceSuffix: "",
    features: [
      "Instant response system",
      "Structured enquiry handling",
      "Lead qualification flow",
      "Basic follow-up sequence",
    ],
    popular: false,
  },
  {
    id: "guide",
    tag: "Guide",
    accent: "#d4a44c",
    bestFor: "Best for: Low conversion from enquiries",
    outcome: "2–3x higher lead-to-sale conversion",
    strike: "₹9,999",
    price: "₹7,999",
    priceSuffix: "",
    features: [
      "Everything in Initiate, plus:",
      "Guided conversation flow",
      "Lead segmentation",
      "Multi-step follow-up",
      "Nurture + conversion path",
    ],
    popular: true,
  },
  {
    id: "core",
    tag: "Core",
    accent: "#c94b4b",
    bestFor: "Best for: Revenue not scaling despite steady leads",
    outcome: "20–40% more revenue from the same pipeline",
    strike: "₹14,999",
    price: "₹10,999",
    priceSuffix: "",
    features: [
      "Everything in Guide, plus:",
      "Website integration",
      "CRM integration",
      "Payment + billing",
      "Revenue expansion flows",
    ],
    popular: false,
  },
  {
    id: "pulse",
    tag: "Pulse",
    accent: "#2d7a5f",
    bestFor: "Best for: Improving performance without increasing spend",
    outcome: "Month-on-month improvement in conversion rates",
    strike: "₹20,000",
    price: "₹10,000",
    priceSuffix: "/month",
    features: [
      "Monthly audit",
      "1 high-impact campaigns",
      "Message optimisation",
      "A/B Testing",
    ],
    popular: false,
  },
];

export default function Packages() {
  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-20 lg:py-28"
      style={{ background: "#ffffff" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.1]"
            data-testid="packages-headline"
          >
            Choose how you want to{" "}
            <span className="font-display-italic">fix your WhatsApp.</span>
          </h2>
          <p className="mt-5 text-lg" style={{ color: "var(--text-muted)" }}>
            You don’t need more tools. You need the right level of system
            inside your business.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {packages.map((p) => (
            <div
              key={p.id}
              data-testid={`package-${p.id}`}
              className={`relative rounded-3xl p-7 lg:p-8 flex flex-col ${
                p.popular
                  ? "popular-ring bg-white card-glow"
                  : "bg-white card-glow"
              }`}
              style={
                p.popular
                  ? {}
                  : {
                      border: "1px solid var(--line)",
                      boxShadow:
                        "0 20px 40px -30px rgba(31,35,48,0.12), 0 2px 6px -2px rgba(31,35,48,0.04)",
                    }
              }
            >
              {p.popular && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] font-bold inline-flex items-center gap-1.5"
                  style={{ background: "var(--gold)", color: "var(--ink)" }}
                  data-testid="popular-badge"
                >
                  <Star size={12} fill="currentColor" />
                  Most Popular
                </div>
              )}

              <div
                className="text-xs font-bold uppercase tracking-[0.22em]"
                style={{ color: p.accent }}
              >
                Conversio™ {p.tag}
              </div>
              <h3 className="font-display text-3xl mt-3 text-[var(--ink)] leading-tight">
                {p.tag}
              </h3>
              <div
                className="text-sm mt-2"
                style={{ color: "var(--text-muted)" }}
              >
                {p.bestFor}
              </div>

              {/* Pricing */}
              <div
                className="mt-5 flex items-baseline gap-3 flex-wrap"
                data-testid={`package-price-${p.id}`}
              >
                <span
                  className="line-through text-base"
                  style={{ color: "var(--text-muted)" }}
                >
                  {p.strike}
                </span>
                <span className="font-display text-[38px] leading-none text-[var(--ink)]">
                  {p.price}
                </span>
                {p.priceSuffix && (
                  <span
                    className="ml-auto text-sm font-medium"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {p.priceSuffix}
                  </span>
                )}
              </div>

              <div
                className="mt-5 rounded-xl px-4 py-3 text-sm font-display-italic"
                style={{
                  background: "var(--cream-soft)",
                  color: "var(--ink)",
                }}
              >
                → {p.outcome}
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-3 items-start">
                    <span
                      className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: "rgba(37,211,102,0.14)" }}
                    >
                      <Check
                        size={12}
                        strokeWidth={3}
                        style={{ color: "var(--whatsapp-dark)" }}
                      />
                    </span>
                    <span className="text-[15px] text-[var(--ink)]">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`package-cta-${p.id}`}
                className="mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02]"
                style={{ background: "var(--whatsapp)" }}
              >
                <MessageCircle size={15} strokeWidth={2.5} />
                Chat on WhatsApp
                <ArrowRight size={14} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
